/**
 * Architecture tab — extra tools completing the BIM ribbon.
 *
 * Covers the dropdown-panel members of Build / Circulation / Room & Area /
 * Curtain System / Openings / Datum / Work Plane that had no tool classes:
 * wall sweeps & reveals, roof by extrusion/face + soffit/fascia/gutter,
 * floor/slab edge & by-face, automatic & by-face ceilings, stair by sketch,
 * railing on host, room separator, area plan/boundary/tag, curtain grid,
 * mullion, curtain system on mass, model in-place, dormer opening,
 * reference plane and the work-plane viewer.
 *
 * Every tool writes real elements through bimStore.addElement/updateElement
 * so they participate in undo, selection and the contextual Modify tab.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool, ToolContext } from '../../tools/BaseTool';
import { Element, View, Level, StructuralOpeningElement } from '../../core/types';
import { polygonArea } from '../../utils/roomBoundary';
import { createCeiling } from '../CeilingTool';
import { createRoof } from '../RoofTool';
import { createCurtainWall } from '../CurtainWallTool';
import { createWall } from '../WallTool';
import { RailingElement } from '../RailingTool';
import { ColumnTool } from '../ColumnTool';
import i18n from '../../i18n';

// ==================== Shared helpers ====================

/** Closed polygon preview (magenta/green sketch lines) for click-sketch tools. */
function pushPolygonPreview(
    ctx: ToolContext,
    points: THREE.Vector3[],
    current: THREE.Vector3 | null,
    color: string
): void {
    if (points.length > 0 && current) {
        ctx.appStore.setToolPreview({
            type: 'polygon',
            points: [...points, current],
            closed: false,
            color,
        });
    } else {
        ctx.appStore.setToolPreview(null);
    }
}

/** Offset a closed polygon outward (away from its centroid) by `dist` meters. */
function offsetPolygonOutward(
    boundary: [number, number][],
    dist: number
): [number, number][] {
    const cx = boundary.reduce((s, p) => s + p[0], 0) / boundary.length;
    const cy = boundary.reduce((s, p) => s + p[1], 0) / boundary.length;
    return boundary.map((p) => {
        const vx = p[0] - cx;
        const vy = p[1] - cy;
        const len = Math.hypot(vx, vy) || 1;
        return [p[0] + (vx / len) * dist, p[1] + (vy / len) * dist] as [number, number];
    });
}

/** First project level name (or 'Level 1' when no levels exist). */
function firstLevelName(ctx: ToolContext): string {
    let name = 'Level 1';
    let min = Infinity;
    ctx.bimStore.project.levels.forEach((l) => {
        if (l.elevation < min) {
            min = l.elevation;
            name = l.name;
        }
    });
    return name;
}

/** Element categories that expose a horizontal footprint for By-Face tools. */
const FACE_HOST_CATEGORIES = ['Mass', 'Roofs', 'Floors', 'Ceilings', 'Walls'];

// ==================== Wall profile tools (sweep / reveal) ====================

/**
 * Base for Wall Sweep / Wall Reveal: click a wall to run a profile band
 * along it at the configured height. Sweeps protrude from the face,
 * reveals are recessed into it. Rendered as a thin box band hugging the
 * wall; the element is hosted (removed with the wall via constraints).
 */
abstract class WallProfileBandTool extends BaseTool {
    cursor = 'crosshair';
    abstract readonly bandCategory: 'Wall Sweeps' | 'Wall Reveals';
    abstract readonly label: string;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.profilePickWall', { label: this.label }), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Walls');
        if (!hit) {
            this.notify(i18n.t('tools.notify.noWallUnderTheCursorClick'), 'warning');
            return;
        }

        const wallGeom = (hit.element as any).geometryData;
        if (!wallGeom?.start || !wallGeom?.end) return;

        const props = this.context.bimStore.toolProperties;
        const heightM = (props.wallSweepHeight ?? 1000) / 1000;
        const protrude = (props.wallSweepOffset ?? (this.bandCategory === 'Wall Sweeps' ? 50 : -50)) / 1000;
        const thicknessM = wallGeom.thickness ?? 0.2;
        const wallHeight = wallGeom.height ?? 3;
        const baseZ = wallGeom.baseOffset ?? 0;
        const z = Math.min(baseZ + heightM, baseZ + wallHeight - 0.05);

        const start = new THREE.Vector3(...wallGeom.start);
        const end = new THREE.Vector3(...wallGeom.end);
        const center = start.clone().add(end).multiplyScalar(0.5);
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const length = start.distanceTo(end);

        // Band cross-section: recess sweeps sit inside the face line, protruding
        // sweeps extend past it. The band is centered on the wall face.
        const faceSign = protrude >= 0 ? 1 : -1;
        const depth = Math.abs(protrude);
        const bandThickness = Math.max(depth, 0.01);

        const element: Element = {
            id: uuidv4(),
            category: this.bandCategory,
            type: `${this.label}: ${props.wallSweepProfile ?? 'Custom'}`,
            name: this.label,
            parameters: {
                'Host': hit.element.id,
                'Height From Base': `${(heightM * 1000).toFixed(0)} mm`,
                'Offset': `${(protrude * 1000).toFixed(0)} mm`,
                'Profile': props.wallSweepProfile ?? 'Custom',
                'Length': `${length.toFixed(2)} m`,
            },
            constraints: [
                { type: 'HOST_CONTAINED', targetId: hit.element.id, data: {} },
            ],
            geometryData: {
                hostWallId: hit.element.id,
                start: [start.x, start.y, z],
                end: [end.x, end.y, z],
                width: thicknessM,
                bandThickness,
                // Lateral center offset of the band relative to the wall face.
                faceOffset: faceSign * (thicknessM / 2 + protrude / 2) - faceSign * protrude / 2,
                protrude,
            },
        };

        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.profilePlaced', { label: this.label }), 'success');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Walls');
        if (hit) {
            this.context.appStore.setToolPreview({
                type: 'line',
                start: hit.point.clone().setZ(hit.point.z),
                end: hit.point.clone().add(new THREE.Vector3(0, 0.001, 0)),
                color: this.bandCategory === 'Wall Sweeps' ? '#2f7bd6' : '#d65f9e',
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(): void {}
}

export class WallSweepToolClass extends WallProfileBandTool {
    id = 'wall_sweep';
    name = 'Wall Sweep';
    readonly bandCategory = 'Wall Sweeps' as const;
    readonly label = 'Wall Sweep';
}

export class WallRevealToolClass extends WallProfileBandTool {
    id = 'wall_reveal';
    name = 'Wall Reveal';
    readonly bandCategory = 'Wall Reveals' as const;
    readonly label = 'Wall Reveal';
}

// ==================== Roof family extras ====================

/**
 * Roof by Extrusion: sketch an open profile polyline in the working plane
 * (click-click… Enter); the profile is swept horizontally into a barrel /
 * custom-form roof stored as a 'Roofs' element with roofType 'Extrusion'.
 */
export class RoofByExtrusionToolClass extends BaseTool {
    id = 'roof_by_extrusion';
    name = 'Roof By Extrusion';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        this.points.push(this.snapToGrid(point));
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 2) {
            this.finish();
        } else if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.roofByExtrusionSketchTheProfile'), 'info');
    }

    private updatePreview() {
        if (!this.context) return;
        pushPolygonPreview(this.context, this.points, this.currentPoint, '#b5533c');
    }

    private finish() {
        if (!this.context) return;
        // Derive a footprint: profile polyline projected on XY, closed with a
        // baseline; the extrusion depth is the horizontal extent (Y size).
        const xs = this.points.map((p) => p.x);
        const ys = this.points.map((p) => p.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        if (maxX - minX < 0.1) {
            this.notify(i18n.t('tools.notify.profileTooNarrowToExtrude'), 'warning');
            return;
        }
        const footprint: [number, number][] = [
            [minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY],
        ];
        const props = this.context.bimStore.toolProperties;
        const roof = createRoof(footprint, {
            roofType: 'Basic Roof: Extrusion - Generic',
            slope: props.roofSlopeAngle ?? 0,
            overhang: 0,
            levelId: firstLevelName(this.context),
            offset: 0,
            cutoff: 'None',
            definesSlope: footprint.map(() => false),
        });
        roof.type = 'Roof by Extrusion';
        (roof.geometryData as any).extrusionProfile = this.points.map(
            (p) => [p.x, p.y, p.z] as [number, number, number]
        );
        this.context.bimStore.addElement(roof);
        this.notify(i18n.t('tools.notify.roofByExtrusionCreated'), 'success');
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }
}

/**
 * By-Face conversion base: click a host element (Mass/Roof/Floor/Ceiling)
 * and create a new horizontal component from its footprint at the clicked
 * elevation — mirrors BIM's "By Face" behavior on mass surfaces.
 */
abstract class ByFaceComponentTool extends BaseTool {
    cursor = 'crosshair';
    abstract readonly targetLabel: string;
    abstract readonly targetCategory: 'Roofs' | 'Floors' | 'Ceilings';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.byFacePick', { label: this.targetLabel }), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => FACE_HOST_CATEGORIES.includes(el.category));
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickAMassFloorCeilingOr'), 'warning');
            return;
        }

        const host = hit.element;
        const geom = (host as any).geometryData ?? {};
        let footprint: [number, number][] | null = null;

        if (Array.isArray(geom.footprint) && geom.footprint.length >= 3) {
            footprint = geom.footprint;
        } else if (Array.isArray(geom.boundary) && geom.boundary.length >= 3) {
            footprint = geom.boundary;
        } else if (geom.start && geom.end) {
            // Wall face: footprint is the wall's rectangular face extended
            // along its normal by the thickness.
            const a = new THREE.Vector2(...geom.start);
            const b = new THREE.Vector2(...geom.end);
            const dir = b.clone().sub(a);
            const len = dir.length();
            if (len < 0.1) return;
            dir.normalize();
            const n = new THREE.Vector2(-dir.y, dir.x).multiplyScalar((geom.thickness ?? 0.2) / 2);
            footprint = [
                [a.x + n.x, a.y + n.y],
                [b.x + n.x, b.y + n.y],
                [b.x - n.x, b.y - n.y],
                [a.x - n.x, a.y - n.y],
            ];
        }

        if (!footprint) {
            this.notify(i18n.t('tools.notify.theClickedHostHasNoUsable'), 'error');
            return;
        }

        const baseZ = hit.point.z;
        this.createComponent(footprint, baseZ, host.id);
        this.notify(i18n.t('tools.notify.byFaceCreated', { label: this.targetLabel }), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}

    protected abstract createComponent(footprint: [number, number][], baseZ: number, hostId: string): void;
}

export class RoofByFaceToolClass extends ByFaceComponentTool {
    id = 'roof_by_face';
    name = 'Roof By Face';
    readonly targetLabel = 'Roof';
    readonly targetCategory = 'Roofs' as const;

    protected createComponent(footprint: [number, number][], baseZ: number, hostId: string): void {
        if (!this.context) return;
        const props = this.context.bimStore.toolProperties;
        const roof = createRoof(footprint, {
            roofType: 'Basic Roof: By Face',
            slope: props.roofSlopeAngle ?? 0,
            overhang: props.roofOverhang ?? 0,
            levelId: firstLevelName(this.context),
            offset: baseZ * 1000,
            cutoff: 'None',
            definesSlope: footprint.map(() => false),
        });
        roof.type = 'Roof by Face';
        (roof.parameters as any)['Host'] = hostId;
        this.context.bimStore.addElement(roof);
    }
}

export class FloorByFaceToolClass extends ByFaceComponentTool {
    id = 'floor_by_face';
    name = 'Floor By Face';
    readonly targetLabel = 'Floor';
    readonly targetCategory = 'Floors' as const;

    protected createComponent(footprint: [number, number][], baseZ: number, hostId: string): void {
        if (!this.context) return;
        const element: Element = {
            id: uuidv4(),
            category: 'Floors',
            type: 'Floor: By Face',
            name: 'Floor by Face',
            parameters: {
                'Host': hostId,
                'Elevation': `${baseZ.toFixed(2)} m`,
                'Area': `${polygonArea(footprint).toFixed(2)} m²`,
                'Thickness': '0.20 m',
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: hostId, data: {} }],
            geometryData: {
                boundary: footprint,
                thickness: 0.2,
                layers: [{ function: 'Structure', materialId: 'Concrete', thickness: 0.2, wraps: false }],
                levelId: firstLevelName(this.context),
                heightOffsetFromLevel: baseZ,
            },
        };
        this.context.bimStore.addElement(element);
    }
}

export class CeilingByFaceToolClass extends ByFaceComponentTool {
    id = 'ceiling_by_face';
    name = 'Ceiling By Face';
    readonly targetLabel = 'Ceiling';
    readonly targetCategory = 'Ceilings' as const;

    protected createComponent(footprint: [number, number][], baseZ: number, hostId: string): void {
        if (!this.context) return;
        const ceiling = createCeiling(footprint, {
            ceilingType: 'Compound Ceiling: Plain',
            levelId: firstLevelName(this.context),
            offset: baseZ * 1000,
        });
        ceiling.type = 'Ceiling by Face';
        (ceiling.parameters as any)['Host'] = hostId;
        this.context.bimStore.addElement(ceiling);
    }
}

/**
 * Wall by Face: click a mass, each footprint edge becomes a full-height
 * wall — the BIM "Wall by Face" conversion of mass boundaries.
 */
export class WallByFaceToolClass extends BaseTool {
    id = 'wall_by_face';
    name = 'Wall By Face';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.wallByFaceClickAMass'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Mass' || el.category === 'Structural In-Place');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickAMassFaceToConvert'), 'warning');
            return;
        }
        const geom = (hit.element as any).geometryData ?? {};
        const footprint: [number, number][] | undefined = geom.footprint;
        if (!footprint || footprint.length < 2) {
            this.notify(i18n.t('tools.notify.theMassHasNoFootprintEdges'), 'error');
            return;
        }
        const height = geom.height ?? 5;
        const elevation = geom.elevation ?? 0;
        const props = this.context.bimStore.toolProperties;
        const thickness = props.wallThicknessForByFace ?? 0.2;

        let created = 0;
        for (let i = 0; i < footprint.length; i++) {
            const a = footprint[i];
            const b = footprint[(i + 1) % footprint.length];
            const start = new THREE.Vector3(a[0], a[1], elevation);
            const end = new THREE.Vector3(b[0], b[1], elevation);
            if (start.distanceTo(end) < 0.1) continue;
            const wall = createWall(start, end, {
                wallType: 'Basic Wall: Generic - 200mm',
                height: height * 1000,        // createWall expects millimeters
                baseOffset: elevation * 1000, // ditto
                topOffset: 0,
                locationLine: 'WallCenterline',
                chain: false,
                structural: false,
                baseConstraint: firstLevelName(this.context),
                topConstraint: 'Unconnected',
            }, this.context.bimStore.project.levels);
            if (!wall) continue;
            (wall.geometryData as any).thickness = thickness;
            wall.type = 'Wall by Face';
            (wall.parameters as any)['Host'] = hit.element.id;
            this.context.bimStore.addElement(wall);
            created++;
        }
        if (created > 0) {
            this.notify(i18n.t('tools.notify.wallsFromMass', { count: created }), 'success');
        }
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

/**
 * Soffit / Fascia / Gutter: pick a roof, the eave band along its footprint
 * is generated. All three share the edge-band shape: a polyline path with
 * a cross-section drawn by EdgeBandRenderer.
 */
abstract class RoofEaveComponentTool extends BaseTool {
    cursor = 'crosshair';
    abstract readonly componentKind: 'Soffit' | 'Fascia' | 'Gutter';
    abstract readonly bandCategory: 'Soffits' | 'Fascias' | 'Gutters';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.roofComponentPick', { kind: this.componentKind }), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Roofs');
        if (!hit) {
            this.notify(i18n.t('tools.notify.roofAddPick', { kind: this.componentKind.toLowerCase() }), 'warning');
            return;
        }
        const geom = (hit.element as any).geometryData;
        const footprint: [number, number][] | undefined = geom?.footprint;
        if (!footprint || footprint.length < 3) {
            this.notify(i18n.t('tools.notify.theClickedRoofHasNoFootprint'), 'error');
            return;
        }

        const props = this.context.bimStore.toolProperties;
        const overhangM = (geom.overhang ?? 0) > 0 ? geom.overhang : (props.roofOverhang ?? 450) / 1000;
        const eaveZ = geom.baseOffsetFromLevel ?? 0;
        // Outer (overhang) edge of the roof.
        const eaveLine = offsetPolygonOutward(footprint, overhangM);

        const path: [number, number, number][] = eaveLine.map((p) => [p[0], p[1], eaveZ]);
        if (path.length > 1) path.push([path[0][0], path[0][1], path[0][2]]); // close loop

        const element: Element = {
            id: uuidv4(),
            category: this.bandCategory,
            type: `Roof ${this.componentKind}`,
            name: `${this.componentKind}`,
            parameters: {
                'Host': hit.element.id,
                'Width': this.componentKind === 'Gutter' ? '0.15 m' : '0.30 m',
                'Offset From Roof Edge': `${overhangM.toFixed(2)} m`,
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: hit.element.id, data: {} }],
            geometryData: {
                hostRoofId: hit.element.id,
                path,
                width: this.componentKind === 'Gutter' ? 0.15 : 0.3,
                thickness: this.componentKind === 'Fascia' ? 0.02 : 0.015,
                height: this.componentKind === 'Fascia' ? 0.2 : 0.1,
                kind: this.componentKind,
            },
        };

        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.roofComponentAdded', { kind: this.componentKind }), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

export class SoffitToolClass extends RoofEaveComponentTool {
    id = 'soffit';
    name = 'Roof Soffit';
    readonly componentKind = 'Soffit' as const;
    readonly bandCategory = 'Soffits' as const;
}

export class FasciaToolClass extends RoofEaveComponentTool {
    id = 'fascia';
    name = 'Roof Fascia';
    readonly componentKind = 'Fascia' as const;
    readonly bandCategory = 'Fascias' as const;
}

export class GutterToolClass extends RoofEaveComponentTool {
    id = 'gutter';
    name = 'Roof Gutter';
    readonly componentKind = 'Gutter' as const;
    readonly bandCategory = 'Gutters' as const;
}

/** Slab Edge: pick a floor, the boundary edge profile wraps its perimeter. */
export class SlabEdgeToolClass extends BaseTool {
    id = 'slab_edge';
    name = 'Slab Edge';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.slabEdgeClickAFloorTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Floors');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickAFloorSlabToAdd'), 'warning');
            return;
        }
        const geom = (hit.element as any).geometryData;
        const boundary: [number, number][] | undefined = geom?.boundary;
        if (!boundary || boundary.length < 3) return;

        const thickness = geom.thickness ?? 0.2;
        const levelZ = geom.heightOffsetFromLevel ?? 0;
        const path: [number, number, number][] = boundary.map((p) => [p[0], p[1], levelZ - thickness / 2]);
        path.push([path[0][0], path[0][1], path[0][2]]);

        const element: Element = {
            id: uuidv4(),
            category: 'Slab Edges',
            type: 'Slab Edge: Fascia 50x200',
            name: 'Slab Edge',
            parameters: {
                'Host': hit.element.id,
                'Width': '0.05 m',
                'Height': `${(thickness * 1000).toFixed(0)} mm`,
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: hit.element.id, data: {} }],
            geometryData: {
                hostFloorId: hit.element.id,
                path,
                width: 0.05,
                thickness: 0.02,
                height: thickness,
                kind: 'SlabEdge',
            },
        };
        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.slabEdgeAddedAroundFloorPerimeter'), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

// ==================== Circulation extras ====================

/**
 * Stair by Sketch: draw the stair centerline/boundary (click-click, Enter).
 * Risers are generated automatically at the configured tread depth between
 * the base and next level.
 */
export class StairBySketchToolClass extends BaseTool {
    id = 'stair_by_sketch';
    name = 'Stair By Sketch';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        this.points.push(this.snapToGrid(point));
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 2) {
            this.finish();
        } else if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.stairBySketchClickTheRun'), 'info');
    }

    private updatePreview() {
        if (!this.context) return;
        pushPolygonPreview(this.context, this.points, this.currentPoint, '#2f7bd6');
    }

    private finish() {
        if (!this.context) return;
        const path = this.points.map((p) => [p.x, p.y, p.z] as [number, number, number]);
        let runLength = 0;
        for (let i = 1; i < path.length; i++) {
            runLength += Math.hypot(path[i][0] - path[i - 1][0], path[i][1] - path[i - 1][1]);
        }
        const tread = 0.28;
        const riserHeight = 0.18;
        const riserCount = Math.max(2, Math.round((runLength / tread) + 1));
        const stairHeight = (riserCount - 1) * riserHeight;

        const stair: Element = {
            id: uuidv4(),
            category: 'Stairs',
            type: 'Stair by Sketch: Assembled',
            name: 'Sketch Stair',
            parameters: {
                'Base Level': firstLevelName(this.context),
                'Top Level': 'Next Level',
                'Riser Count': riserCount,
                'Total Height': `${stairHeight.toFixed(2)} m`,
            },
            constraints: [],
            geometryData: {
                baseLevelId: firstLevelName(this.context),
                topLevelId: 'Next Level',
                desiredRiserHeight: riserHeight,
                desiredTreadDepth: tread,
                actualRiserHeight: riserHeight,
                actualTreadDepth: tread,
                riserCount,
                width: 1.2,
                runs: [{
                    id: uuidv4(),
                    type: 'Run',
                    style: 'Straight',
                    path,
                    width: 1.2,
                    riserCount,
                    baseElevation: 0,
                    topElevation: stairHeight,
                    beginWithRiser: true,
                    endWithRiser: true,
                    shape: 'Straight',
                    geometryData: { path, width: 1.2, riserHeight, treadDepth: tread, riserCount },
                }],
                landings: [],
                components: [],
            },
        };
        this.context.bimStore.addElement(stair);
        this.notify(i18n.t('tools.notify.stairGenerated', { count: riserCount }), 'success');
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }
}

/**
 * Railing Place on Host: pick a stair, ramp or floor; the railing is mounted
 * along the host edges (run sides for stairs, both flanks for ramps, the
 * boundary loop for floors).
 */
export class RailingOnHostToolClass extends BaseTool {
    id = 'railing_on_host';
    name = 'Railing On Host';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.placeOnHostClickAStair'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) =>
            ['Stairs', 'Ramps', 'Floors', 'Roofs'].includes(el.category));
        if (!hit) {
            this.notify(i18n.t('tools.notify.noStairRampOrFloorUnder'), 'warning');
            return;
        }

        const paths: [number, number, number][][] = [];
        const host = hit.element;
        const geom = (host as any).geometryData ?? {};
        const height = 1.1;

        if (host.category === 'Stairs' && Array.isArray(geom.runs)) {
            for (const run of geom.runs) {
                const runPath: [number, number, number][] = run.path ?? [];
                if (runPath.length < 2) continue;
                // Offset the run centerline by half the run width on both sides.
                for (const side of [-1, 1]) {
                    const p0 = new THREE.Vector3(...runPath[0]);
                    const p1 = new THREE.Vector3(...runPath[runPath.length - 1]);
                    const dir = p1.clone().sub(p0);
                    const flat = new THREE.Vector2(dir.x, dir.y).normalize();
                    const normal = new THREE.Vector2(-flat.y, flat.x).multiplyScalar(side * ((run.width ?? 1) / 2));
                    paths.push(runPath.map((p: [number, number, number], i: number) => {
                        const t = runPath.length > 1 ? i / (runPath.length - 1) : 0;
                        return [p[0] + normal.x, p[1] + normal.y, p[2] + t * (run.topElevation - run.baseElevation) + height / 2] as [number, number, number];
                    }));
                }
            }
        } else if (host.category === 'Ramps' && geom.start && geom.end) {
            const a = new THREE.Vector3(...geom.start);
            const b = new THREE.Vector3(...geom.end);
            const dir = new THREE.Vector2(b.x - a.x, b.y - a.y).normalize();
            const normal = new THREE.Vector2(-dir.y, dir.x).multiplyScalar((geom.width ?? 1) / 2);
            for (const side of [-1, 1]) {
                paths.push([
                    [a.x + normal.x * side, a.y + normal.y * side, a.z + height / 2],
                    [b.x + normal.x * side, b.y + normal.y * side, b.z + height / 2],
                ]);
            }
        } else if ((host.category === 'Floors' || host.category === 'Roofs') && Array.isArray(geom.boundary ?? geom.footprint)) {
            const boundary = (geom.boundary ?? geom.footprint) as [number, number][];
            const loop = boundary.map((p) => [p[0], p[1], (geom.heightOffsetFromLevel ?? geom.baseOffsetFromLevel ?? 0) + height / 2] as [number, number, number]);
            loop.push(loop[0]);
            paths.push(loop);
        }

        if (paths.length === 0) {
            this.notify(i18n.t('tools.notify.theHostGeometryHasNoRailable'), 'error');
            return;
        }

        for (const path of paths) {
            if (path.length < 2) continue;
            const railing: RailingElement = {
                id: uuidv4(),
                category: 'Railings',
                type: 'Railing: 1100mm',
                name: 'Railing on Host',
                parameters: {
                    'Host': host.id,
                    'Height': `${height.toFixed(2)} m`,
                    'Placement': 'On Host',
                },
                constraints: [{ type: 'HOST_CONTAINED', targetId: host.id, data: {} }],
                geometryData: {
                    path,
                    height,
                    offset: 0,
                    levelId: firstLevelName(this.context),
                },
            };
            this.context.bimStore.addElement(railing);
        }
        this.notify(i18n.t('tools.notify.railingMounted', { count: paths.length }), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

// ==================== Room & Area extras ====================

/** Room Separator: a virtual two-end line dividing open plan areas. */
export class RoomSeparatorToolClass extends BaseTool {
    id = 'room_separator';
    name = 'Room Separator';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.roomSeparatorClickTwoPointsTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            return;
        }

        const start = this.startPoint;
        this.startPoint = null;
        const element: Element = {
            id: uuidv4(),
            category: 'Room Separators',
            type: 'Room Separator',
            name: 'Room Separator',
            parameters: {
                'Level': firstLevelName(this.context),
                'Length': `${start.distanceTo(snapped).toFixed(2)} m`,
            },
            constraints: [],
            geometryData: {
                start: [start.x, start.y, start.z],
                end: [snapped.x, snapped.y, snapped.z],
                height: 2.4,
            },
        };
        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.roomSeparatorPlaced'), 'success');
        this.context.appStore.setToolPreview(null);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (!point) return;
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.startPoint,
            end: this.snapToGrid(point),
            color: '#2f7bd6',
        });
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.startPoint) {
            this.startPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }
}

/** Area Plan: derive a plan-type view dedicated to area take-off. */
export class AreaPlanToolClass extends BaseTool {
    id = 'area_plan';
    name = 'Area Plan';
    cursor = 'default';

    protected onActivate(): void {
        if (!this.context) return;
        const selection = this.context.appStore.selectedIds;
        // Prefer a selected level; otherwise base the plan on the lowest level.
        let chosen: Level | undefined;
        this.context.bimStore.project.levels.forEach((l) => {
            if (selection.includes(l.id) && !chosen) chosen = l;
        });
        if (!chosen) {
            this.context.bimStore.project.levels.forEach((l) => {
                if (!chosen || l.elevation < chosen.elevation) chosen = l;
            });
        }
        if (!chosen) {
            this.notify(i18n.t('tools.notify.noLevelsInTheProjectCreate'), 'error');
            this.exit();
            return;
        }
        const typedLevel = chosen as Level;
        const view: View = {
            id: uuidv4(),
            name: `Area Plan - ${typedLevel.name}`,
            type: 'AreaPlan',
            discipline: 'Architectural',
            scale: 100,
            detailLevel: 'Medium',
            visibility: {},
            filters: [],
            associatedLevelId: typedLevel.id,
            viewRange: { top: 2.3, cutPlane: 1.2, bottom: 0, depth: 0 },
        };
        this.context.bimStore.addView(view);
        this.context.bimStore.setActiveView(view.id);
        this.notify(i18n.t('tools.notify.areaPlanCreated', { levelName: typedLevel.name }), 'success');
        this.exit();
    }

    private exit(): void {
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void {}
    onPointerMove(): void {}
    onPointerUp(): void {}
}

/** Area Boundary Line: open polygon stroke marking an area boundary. */
export class AreaBoundaryToolClass extends BaseTool {
    id = 'area_boundary';
    name = 'Area Boundary';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.areaBoundarySketchTheBoundaryPolygon'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        this.points.push(this.snapToGrid(point));
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 2) {
            this.finish();
        } else if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview() {
        if (!this.context) return;
        pushPolygonPreview(this.context, this.points, this.currentPoint, '#d9832e');
    }

    private finish() {
        if (!this.context) return;
        const boundary = this.points.map((p) => [p.x, p.y] as [number, number]);
        const closed = boundary.length >= 3;
        const element: Element = {
            id: uuidv4(),
            category: closed ? 'Areas' : 'Area Boundaries',
            type: 'Area Boundary Line',
            name: 'Area Boundary',
            parameters: {
                'Level': firstLevelName(this.context),
                'Compute': closed ? 'True' : 'Open',
                'Area': closed ? `${polygonArea(boundary).toFixed(2)} m²` : '—',
            },
            constraints: [],
            geometryData: closed
                ? { boundary, levelId: firstLevelName(this.context), heightOffsetFromLevel: 0 }
                : { path: this.points.map((p) => [p.x, p.y, p.z] as [number, number, number]) },
        };
        this.context.bimStore.addElement(element);
        this.notify(closed ? 'Area boundary closed; area computed.' : 'Open boundary segment placed.', 'success');
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }
}

/** Area Tag: click an Areas element to pin its computed m² tag. */
export class AreaTagToolClass extends BaseTool {
    id = 'area_tag';
    name = 'Area Tag';
    cursor = 'text';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.areaTagClickAnAreaBounded'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Areas' || el.category === 'Rooms');
        if (!hit) {
            this.notify(i18n.t('tools.notify.noAreaUnderTheCursor'), 'warning');
            return;
        }
        const target = hit.element;
        const area = target.parameters['Area'] ?? '—';
        const pos = target.geometryData?.position ?? [hit.point.x, hit.point.y, hit.point.z];

        const tag: Element = {
            id: uuidv4(),
            category: 'Tags',
            type: 'Tag: Areas',
            name: `Area Tag ${area}`,
            parameters: {
                'Target Category': target.category,
                'Target Id': target.id,
                'Value': String(area),
                'Parameter': 'Area',
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: target.id, data: {} }],
            geometryData: { position: pos, targetId: target.id },
        };
        this.context.bimStore.addElement(tag);
        this.notify(i18n.t('tools.notify.areaTagPlaced'), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

// ==================== Curtain system extras ====================

/**
 * Curtain Grid: click a curtain wall to insert a vertical grid line at the
 * clicked station; Shift-click inserts a horizontal grid at the clicked
 * height. The solver re-lays panels around the new grid.
 */
export class CurtainGridToolClass extends BaseTool {
    id = 'curtain_grid';
    name = 'Curtain Grid';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.curtainGridClickACurtainWall'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Curtain Walls');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickACurtainWallFaceTo'), 'warning');
            return;
        }
        const wall = hit.element;
        const geom = (wall as any).geometryData;
        const start = new THREE.Vector3(...geom.start);
        const end = new THREE.Vector3(...geom.end);
        const length = start.distanceTo(end);
        const dir = end.clone().sub(start).normalize();
        const station = new THREE.Vector3().subVectors(hit.point, start).dot(dir);

        const vertical = !event.nativeEvent.shiftKey;
        const value = vertical
            ? Math.max(0.1, Math.min(length - 0.1, station))
            : Math.max(0.1, Math.min((geom.height ?? 3) - 0.1, hit.point.z - start.z));

        const grids = vertical
            ? [...(geom.verticalGrids ?? []), value].sort((a: number, b: number) => a - b)
            : [...(geom.horizontalGrids ?? []), value].sort((a: number, b: number) => a - b);

        this.context.bimStore.updateElement(wall.id, {
            geometryData: {
                ...geom,
                [vertical ? 'verticalGrids' : 'horizontalGrids']: grids,
            },
        });
        this.notify(i18n.t('tools.notify.curtainGridAdded', { orientation: vertical ? i18n.t('tools.notify.orientVertical') : i18n.t('tools.notify.orientHorizontal'), value: value.toFixed(2) }), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

/** Mullion: click a curtain wall to apply mullions on all its grid lines. */
export class MullionToolClass extends BaseTool {
    id = 'mullion';
    name = 'Mullion';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.mullionClickACurtainWallTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) =>
            el.category === 'Curtain Walls' || el.category === 'Walls');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickACurtainWallOrCurtain'), 'warning');
            return;
        }
        if (hit.element.category !== 'Curtain Walls') {
            this.notify(i18n.t('tools.notify.mullionsCanOnlyBePlacedOn'), 'error');
            return;
        }
        const wall = hit.element;
        const props = this.context.bimStore.toolProperties;
        const geom = { ...(wall as any).geometryData };
        // Ensure a baseline grid exists when none was added with Curtain Grid.
        const start = new THREE.Vector3(...geom.start);
        const end = new THREE.Vector3(...geom.end);
        const length = start.distanceTo(end);
        if (!geom.verticalGrids?.length) {
            const spacing = (props.curtainGridSpacing ?? 1500) / 1000;
            const count = Math.max(1, Math.floor(length / spacing));
            geom.verticalGrids = Array.from({ length: count - 1 }, (_, i) => (i + 1) * (length / count));
        }
        if (!geom.horizontalGrids?.length) {
            const spacing = (props.curtainGridSpacing ?? 1500) / 1000;
            const h = geom.height ?? 3;
            const count = Math.max(1, Math.floor(h / spacing));
            geom.horizontalGrids = Array.from({ length: count - 1 }, (_, i) => (i + 1) * (h / count));
        }
        // Persist the Options Bar mullion size (mm → m) so the
        // CurtainWallSolver renders the frame profile with the chosen width.
        geom.mullionWidth = (props.curtainMullionSize ?? 50) / 1000;
        this.context.bimStore.updateElement(wall.id, {
            geometryData: geom,
            parameters: { ...wall.parameters, 'Mullion Grid Pattern': 'All Grid Lines' },
        });
        this.notify(i18n.t('tools.notify.mullionsAppliedToAllCurtainGrid'), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

/**
 * Curtain System: click a mass, then each footprint edge becomes a curtain
 * wall panel system — the BIM "apply curtain system to mass face" flow.
 */
export class CurtainSystemToolClass extends BaseTool {
    id = 'curtain_system';
    name = 'Curtain System';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.curtainSystemClickAMassTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Mass');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickAMassFaceToApply'), 'warning');
            return;
        }
        const geom = (hit.element as any).geometryData ?? {};
        const footprint: [number, number][] = geom.footprint ?? geom.profilePoints?.map(
            (p: any) => [p.x, p.y] as [number, number]
        );
        if (!footprint || footprint.length < 3) {
            this.notify(i18n.t('tools.notify.theMassHasNoFootprintTo'), 'error');
            return;
        }
        const props = this.context.bimStore.toolProperties;
        const spacing = (props.curtainGridSpacing ?? 1500) / 1000;
        const height = geom.height ?? 5;
        const baseZ = geom.elevation ?? 0;

        let created = 0;
        for (let i = 0; i < footprint.length; i++) {
            const a = footprint[i];
            const b = footprint[(i + 1) % footprint.length];
            const start = new THREE.Vector3(a[0], a[1], baseZ);
            const end = new THREE.Vector3(b[0], b[1], baseZ);
            const wall = createCurtainWall(start, end, {
                height,
                baseOffset: baseZ,
                topOffset: 0,
                baseLevelId: firstLevelName(this.context),
                topLevelId: '',
                gridLayout: 'Fixed Distance',
                verticalGridSpacing: spacing,
                horizontalGridSpacing: spacing,
            });
            (wall.parameters as any)['Host'] = hit.element.id;
            (wall.parameters as any)['Curtain System'] = 'True';
            // Horizontal grids: the factory leaves them empty, add rows here.
            const rows = Math.max(1, Math.floor(height / spacing));
            (wall.geometryData as any).horizontalGrids = Array.from(
                { length: rows - 1 },
                (_, k) => (k + 1) * (height / rows)
            );
            this.context.bimStore.addElement(wall);
            created++;
        }
        this.notify(i18n.t('tools.notify.curtainSystemApplied', { count: created }), 'success');
    }

    onPointerMove(): void {}
    onPointerUp(): void {}
}

// ==================== Openings extras ====================

/** Dormer opening: two-click void placed on a roof surface. */
export class DormerOpeningToolClass extends BaseTool {
    id = 'opening_dormer';
    name = 'Dormer Opening';
    cursor = 'crosshair';

    private firstPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.dormerClickTwoPointsOnThe'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Roofs');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickOnTheRoofSurface'), 'warning');
            return;
        }
        const snapped = this.snapToGrid(hit.point);

        if (!this.firstPoint) {
            this.firstPoint = snapped;
            return;
        }
        const start = this.firstPoint;
        this.firstPoint = null;
        const center = new THREE.Vector3(
            (start.x + snapped.x) / 2,
            (start.y + snapped.y) / 2,
            (start.z + snapped.z) / 2
        );
        const w = Math.max(Math.abs(snapped.x - start.x), 0.4);
        const h = Math.max(Math.abs(snapped.y - start.y), 0.4);
        const element: StructuralOpeningElement = {
            id: uuidv4(),
            category: 'Structural Openings',
            type: 'Dormer Opening',
            name: 'Dormer',
            parameters: {
                'Opening Kind': 'Dormer',
                'Host': hit.element.id,
                'Width': `${(w * 1000).toFixed(0)} mm`,
                'Depth': `${(h * 1000).toFixed(0)} mm`,
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: hit.element.id, data: {} }],
            geometryData: {
                kind: 'Dormer',
                hostId: hit.element.id,
                center: [center.x, center.y, center.z],
                width: w,
                height: 1.2,
                depth: h,
                rotation: 0,
            },
        };
        this.context.bimStore.addElement(element);
        this.notify(i18n.t('tools.notify.dormerOpeningCutIntoTheRoof'), 'success');
        this.context.appStore.setToolPreview(null);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.firstPoint) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        this.context.appStore.setToolPreview({
            type: 'polygon',
            points: [
                new THREE.Vector3(this.firstPoint.x, this.firstPoint.y, this.firstPoint.z),
                new THREE.Vector3(snapped.x, this.firstPoint.y, this.firstPoint.z),
                new THREE.Vector3(snapped.x, snapped.y, this.firstPoint.z),
                new THREE.Vector3(this.firstPoint.x, snapped.y, this.firstPoint.z),
            ],
            color: '#d65fb0',
        });
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.firstPoint) {
            this.firstPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }
}

// ==================== Work Plane extras ====================

/**
 * Work Plane Viewer: one-shot command that frames the active work plane
 * head-on. The viewport listens for 'yap:view-work-plane' and orients the
 * camera along the work plane normal.
 */
export class WorkPlaneViewerToolClass extends BaseTool {
    id = 'work_plane_viewer';
    name = 'Align View to Work Plane';
    cursor = 'default';

    protected onActivate(): void {
        if (!this.context) return;
        const workPlane = this.context.appStore.view.workPlane;
        if (!workPlane) {
            this.notify(i18n.t('tools.notify.noActiveWorkPlaneUseSet'), 'warning');
        } else {
            window.dispatchEvent(new CustomEvent('yap:view-work-plane'));
            this.notify(i18n.t('tools.notify.viewingWorkPlaneHeadOn'), 'success');
        }
        this.context.appStore.setActiveTool('select');
    }

    onPointerDown(): void {}
    onPointerMove(): void {}
    onPointerUp(): void {}
}

// ==================== Structural/architectural column variants ====================

/** Architectural column: the full ColumnTool behavior with an architectural
 *  (non-load-bearing) family preset; shares the 'Structural Columns' render
 *  path like BIM shares its Columns category. */
export class ArchitecturalColumnToolClass extends ColumnTool {
    id = 'column_arch';
    name = 'Architectural Column';

    activate(context: ToolContext): void {
        this.setOptions({
            familyName: 'Architectural Column: Casing',
            typeName: '300 x 300mm Finish',
            profile: { shape: 'Rectangular', width: 300, depth: 300 },
            materialId: 'mat-concrete-40',
            fireproofing: { type: 'None', thickness: 0, fireRating: '' },
        });
        super.activate(context);
    }
}
